/**
 * The timezones an event may be set to.
 *
 * One list, shared by the governance create form and the event Settings tab.
 * Settings took free text until it was pointed here, so `Not/A_Timezone` saved
 * and reported success; every agenda day key is then computed from a zone
 * `zoneinfo` cannot resolve.
 *
 * There are 418 canonical IANA names but only 61 distinct clocks: zones
 * whose offsets agree on every transition through the sampled horizon —
 * every six hours over five years — compute identical day keys and
 * identical notification times for as long as the tz database stays as it
 * is, so the list carries one representative per family, labelled with the
 * family's offset on the event's date — the Berlin entry also stands for Paris,
 * Madrid, Rome and thirty more. A future tz rule change that splits a
 * family splits this list with it. The oddballs survive as their own
 * entries because they really are different clocks: Kathmandu runs on
 * +05:45, the Chatham Islands on +12:45, and Cairo's DST differs from
 * Gaza's by weeks.
 *
 * An event whose stored zone is not on this list keeps it as an extra option
 * on Settings, because a dropdown that silently drops the current value is
 * how an unrelated edit moves every session by hours.
 */
/** One option of the picker: the IANA name stored, and what the list shows. */
export type EventTimezone = { zone: string; label: string };

/**
 * The zones on offer, each with the places it is labelled with. The offset in
 * front of the places is computed for a date by `eventTimezones`, because a
 * fixed one is wrong for half the year: Berlin is GMT+01:00 in winter and
 * GMT+02:00 in summer, and Sydney the other way round.
 */
const TIMEZONE_PLACES: readonly { zone: string; places: string }[] = [
  { zone: "Pacific/Pago_Pago", places: "Pago Pago" },
  { zone: "America/Adak", places: "Adak, Alaska" },
  { zone: "Pacific/Honolulu", places: "Honolulu" },
  { zone: "Pacific/Marquesas", places: "Marquesas Islands" },
  { zone: "America/Anchorage", places: "Anchorage" },
  { zone: "Pacific/Gambier", places: "Gambier Islands" },
  {
    zone: "America/Los_Angeles",
    places: "Los Angeles · San Francisco · Tijuana",
  },
  { zone: "Pacific/Pitcairn", places: "Pitcairn Islands" },
  { zone: "America/Denver", places: "Denver" },
  { zone: "America/Phoenix", places: "Phoenix" },
  { zone: "America/Chicago", places: "Chicago · Winnipeg" },
  { zone: "Pacific/Easter", places: "Easter Island" },
  { zone: "America/Mexico_City", places: "Mexico City · Guatemala" },
  { zone: "America/Bogota", places: "Bogotá · Lima · Panama · Cancún" },
  { zone: "America/New_York", places: "New York · Toronto · Havana" },
  { zone: "America/Caracas", places: "Caracas · La Paz · Puerto Rico" },
  { zone: "America/Halifax", places: "Halifax · Bermuda" },
  { zone: "America/Santiago", places: "Santiago" },
  { zone: "America/St_Johns", places: "Newfoundland" },
  { zone: "America/Miquelon", places: "Saint-Pierre & Miquelon" },
  {
    zone: "America/Sao_Paulo",
    places: "São Paulo · Buenos Aires · Montevideo",
  },
  { zone: "America/Noronha", places: "Fernando de Noronha" },
  { zone: "America/Godthab", places: "Nuuk, Greenland" },
  { zone: "Atlantic/Azores", places: "Azores" },
  { zone: "Atlantic/Cape_Verde", places: "Cape Verde" },
  { zone: "Europe/London", places: "London · Dublin · Lisbon" },
  { zone: "Atlantic/Reykjavik", places: "Reykjavík · Dakar" },
  { zone: "Antarctica/Troll", places: "Troll Station, Antarctica" },
  {
    zone: "Europe/Berlin",
    places: "Berlin · Paris · Madrid · Rome · Amsterdam",
  },
  { zone: "Africa/Lagos", places: "Lagos · Algiers · Tunis · Kinshasa" },
  { zone: "Europe/Athens", places: "Athens · Helsinki · Kiev · Sofia" },
  { zone: "Asia/Beirut", places: "Beirut" },
  { zone: "Africa/Cairo", places: "Cairo" },
  { zone: "Asia/Gaza", places: "Gaza · Hebron" },
  { zone: "Asia/Jerusalem", places: "Jerusalem" },
  { zone: "Africa/Johannesburg", places: "Johannesburg · Tripoli" },
  { zone: "Europe/Moscow", places: "Moscow · Istanbul · Riyadh · Nairobi" },
  { zone: "Asia/Tehran", places: "Tehran" },
  { zone: "Asia/Dubai", places: "Dubai · Mauritius" },
  { zone: "Asia/Kabul", places: "Kabul" },
  { zone: "Asia/Karachi", places: "Karachi · Tashkent" },
  { zone: "Asia/Calcutta", places: "Kolkata · Colombo" },
  { zone: "Asia/Katmandu", places: "Kathmandu" },
  { zone: "Asia/Dhaka", places: "Dhaka · Bishkek" },
  { zone: "Asia/Rangoon", places: "Yangon" },
  { zone: "Asia/Bangkok", places: "Bangkok · Jakarta · Saigon" },
  {
    zone: "Asia/Shanghai",
    places: "Shanghai · Singapore · Hong Kong · Manila",
  },
  { zone: "Australia/Eucla", places: "Eucla, Western Australia" },
  { zone: "Asia/Tokyo", places: "Tokyo · Seoul" },
  { zone: "Australia/Adelaide", places: "Adelaide" },
  { zone: "Australia/Darwin", places: "Darwin" },
  { zone: "Australia/Brisbane", places: "Brisbane · Vladivostok" },
  { zone: "Australia/Sydney", places: "Sydney · Melbourne · Hobart" },
  { zone: "Australia/Lord_Howe", places: "Lord Howe Island" },
  { zone: "Pacific/Norfolk", places: "Norfolk Island" },
  { zone: "Pacific/Noumea", places: "Nouméa · Magadan" },
  { zone: "Pacific/Auckland", places: "Auckland" },
  { zone: "Pacific/Fiji", places: "Fiji · Kamchatka" },
  { zone: "Pacific/Chatham", places: "Chatham Islands" },
  { zone: "Pacific/Apia", places: "Apia · Tonga" },
  { zone: "Pacific/Kiritimati", places: "Kiritimati" },
];

/** Formatters are costly to build and the list asks for 61 at a time. */
const formatters = new Map<string, Intl.DateTimeFormat>();

function wallClockFormatter(zone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(zone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
    formatters.set(zone, formatter);
  }
  return formatter;
}

/**
 * Minutes the zone's clock is ahead of UTC at that instant.
 *
 * Read off the zone's wall clock rather than `timeZoneName: "longOffset"`,
 * which older Safari versions do not support.
 */
export function timezoneOffsetMinutes(zone: string, at: Date): number {
  const parts: Record<string, number> = {};
  for (const part of wallClockFormatter(zone).formatToParts(at)) {
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  }
  const wall = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    // Some engines write midnight as 24 even with h23.
    parts.hour % 24,
    parts.minute,
    parts.second,
  );
  const instant = Math.floor(at.getTime() / 1000) * 1000;
  return Math.round((wall - instant) / 60000);
}

/** "GMT+02:00" for the zone at that instant. */
export function timezoneOffsetLabel(zone: string, at: Date): string {
  const minutes = timezoneOffsetMinutes(zone, at);
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, "0");
  const rest = String(abs % 60).padStart(2, "0");
  return `GMT${sign}${hours}:${rest}`;
}

/**
 * The instant the offsets are shown for: noon UTC on the event's start date
 * when it is a valid "YYYY-MM-DD", otherwise now. Noon UTC falls on that date
 * in nearly every zone on the list. A zone that changes its clock on the
 * event's first day may show either offset, which is right for part of it.
 */
export function timezoneReferenceDate(startDate?: string | null): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate ?? "");
  if (match) {
    const at = new Date(
      Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12),
    );
    if (!Number.isNaN(at.getTime())) return at;
  }
  return new Date();
}

/**
 * The picker's options, labelled and ordered by each zone's offset at `at`.
 *
 * Ordered by that offset too, so in summer London (+01:00) sits with Lagos
 * rather than below Reykjavík. Zones with the same offset keep the list's
 * order.
 */
export function eventTimezones(at: Date = new Date()): EventTimezone[] {
  return TIMEZONE_PLACES.map(({ zone, places }) => ({
    zone,
    places,
    offset: timezoneOffsetMinutes(zone, at),
  }))
    .sort((a, b) => a.offset - b.offset)
    .map(({ zone, places }) => ({
      zone,
      label: `(${timezoneOffsetLabel(zone, at)}) ${places}`,
    }));
}

/** Whether the zone is one the list offers. */
export function isListedTimezone(zone: string): boolean {
  return TIMEZONE_PLACES.some((option) => option.zone === zone);
}

/**
 * More places each zone stands for, so a search finds them.
 *
 * A label can name only a few cities before it stops fitting on a phone, and
 * somebody looking for Seattle has no reason to know it keeps Los Angeles
 * time. Each place here keeps exactly its zone's clock today, which is the
 * same test the list itself was built on. Searching also matches the IANA
 * name and the label, so neither needs repeating here.
 *
 * German names sit beside English ones, since most organisers are German.
 * Accents are stripped before matching, so "Munchen" finds "München", but a
 * spelling with "ue" or "oe" has to be listed on its own.
 *
 * A name two places share carries its qualifier, so a search for it lists
 * both zones and the "Includes" line under each says which place is meant:
 * Washington, D.C. and Washington State; Portland, Oregon and Portland,
 * Maine.
 */
export const TIMEZONE_SEARCH_ALIASES: Readonly<
  Record<string, readonly string[]>
> = {
  "Pacific/Honolulu": ["Hawaii"],
  "America/Anchorage": ["Alaska", "Juneau"],
  "America/Los_Angeles": [
    "Seattle",
    "Washington State",
    "Portland, Oregon",
    "San Diego",
    "San Jose",
    "Las Vegas",
    "Vancouver",
    "California",
    "Kalifornien",
    "Pacific Time",
  ],
  "America/Denver": [
    "Salt Lake City",
    "Calgary",
    "Edmonton",
    "Boise",
    "Mountain Time",
  ],
  "America/Phoenix": ["Arizona"],
  "America/Chicago": [
    "Dallas",
    "Houston",
    "Austin",
    "Minneapolis",
    "New Orleans",
    "Central Time",
  ],
  "America/New_York": [
    "Boston",
    "Washington, D.C.",
    "Portland, Maine",
    "Philadelphia",
    "Atlanta",
    "Miami",
    "Detroit",
    "Montreal",
    "Ottawa",
    "Eastern Time",
  ],
  "America/Sao_Paulo": ["Rio de Janeiro", "Brasília"],
  "Europe/London": [
    "Edinburgh",
    "Manchester",
    "Belfast",
    "United Kingdom",
    "Lissabon",
  ],
  "Europe/Berlin": [
    "Munich",
    "München",
    "Muenchen",
    "Hamburg",
    "Frankfurt",
    "Cologne",
    "Köln",
    "Koeln",
    "Stuttgart",
    "Düsseldorf",
    "Duesseldorf",
    "Leipzig",
    "Dresden",
    "Vienna",
    "Wien",
    "Zurich",
    "Zürich",
    "Zuerich",
    "Geneva",
    "Genf",
    "Bern",
    "Brussels",
    "Brüssel",
    "Luxembourg",
    "Copenhagen",
    "Kopenhagen",
    "Oslo",
    "Stockholm",
    "Prague",
    "Prag",
    "Warsaw",
    "Warschau",
    "Budapest",
    "Milan",
    "Mailand",
    "Barcelona",
    "Germany",
    "Deutschland",
  ],
  "Europe/Athens": [
    "Athen",
    "Bucharest",
    "Bukarest",
    "Kyiv",
    "Kiew",
    "Riga",
    "Tallinn",
    "Vilnius",
  ],
  "Africa/Johannesburg": ["Cape Town", "Kapstadt"],
  "Europe/Moscow": ["Moskau", "Saint Petersburg", "Doha", "Kuwait"],
  "Asia/Dubai": ["Abu Dhabi", "Muscat"],
  "Asia/Calcutta": ["Mumbai", "New Delhi", "Bangalore", "Chennai", "India"],
  "Asia/Bangkok": ["Ho Chi Minh City", "Hanoi"],
  "Asia/Shanghai": ["Beijing", "Peking", "Taipei", "Kuala Lumpur", "Perth"],
  "Asia/Tokyo": ["Osaka"],
  "Australia/Sydney": ["Canberra"],
  "Pacific/Auckland": ["Wellington"],
};

/**
 * Lower case, no accents, no dots ("D.C." reads as "dc"), and IANA's "/" and
 * "_" read as spaces.
 */
function normalise(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\./g, "")
    .toLowerCase()
    .replace(/[/_·(),]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export type TimezoneMatch = EventTimezone & {
  /** The alias that matched when the label itself does not show it. */
  matchedAlias?: string;
};

/**
 * The options whose label, IANA name or aliases contain every word typed.
 *
 * Every word, in any order, so "francisco san" and "san fran" both find Los
 * Angeles. Accents are ignored both ways: "sao paulo" finds São Paulo. The
 * list keeps its own order, which is by offset, because a ranking would
 * move entries around while somebody is still typing. An empty query
 * returns every option.
 */
export function searchTimezones(
  query: string,
  options: readonly EventTimezone[],
): TimezoneMatch[] {
  const words = normalise(query).split(" ").filter(Boolean);
  if (words.length === 0) return [...options];
  const matches: TimezoneMatch[] = [];
  for (const option of options) {
    const visible = normalise(`${option.label} ${option.zone}`);
    if (words.every((word) => visible.includes(word))) {
      matches.push(option);
      continue;
    }
    const aliases = TIMEZONE_SEARCH_ALIASES[option.zone] ?? [];
    const haystack = normalise(`${visible} ${aliases.join(" ")}`);
    if (!words.every((word) => haystack.includes(word))) continue;
    const matchedAlias = aliases.find((alias) =>
      words.some((word) => normalise(alias).includes(word)),
    );
    matches.push({ ...option, matchedAlias });
  }
  return matches;
}

/**
 * A place name for the zone, for a sentence like "Times are Berlin time".
 *
 * The first city on the zone's label when it is on the list, since that is
 * what an organiser picked. Otherwise the last part of the IANA name, which
 * for a stored oddball like "US/Pacific" is still recognisable.
 */
export function timezonePlace(zone: string): string {
  const listed = TIMEZONE_PLACES.find((option) => option.zone === zone);
  if (listed) return listed.places.split(" · ")[0];
  return (zone.split("/").pop() ?? zone).replace(/_/g, " ");
}
